import { useId, CSSProperties, useState } from 'react'

interface SpectralLine {
  wavelength: number
  color: string
}

interface PrismSpectrometerProps {
  angle?: number // 0-180 degrees
  viewingSpectrum?: boolean
  activeGas?: 'hydrogen' | 'helium' | 'neon' | null
  highlightedLine?: SpectralLine
  onAngleChange?: (angle: number) => void
  disabled?: boolean
  className?: string
}

// Spectral lines for different gases
const SPECTRAL_LINES = {
  hydrogen: [
    { wavelength: 656.3, color: '#FF0000', name: 'H-alpha' },    // Red
    { wavelength: 486.1, color: '#00FFFF', name: 'H-beta' },     // Cyan
    { wavelength: 434.0, color: '#8A2BE2', name: 'H-gamma' },    // Blue-violet
    { wavelength: 410.2, color: '#9400D3', name: 'H-delta' }     // Violet
  ],
  helium: [
    { wavelength: 587.6, color: '#FFD700', name: 'Yellow' },     // Yellow
    { wavelength: 501.6, color: '#00FF7F', name: 'Green' },      // Green
    { wavelength: 471.3, color: '#00BFFF', name: 'Blue' },       // Blue
    { wavelength: 447.1, color: '#4169E1', name: 'Blue-violet' } // Blue-violet
  ],
  neon: [
    { wavelength: 640.2, color: '#FF4500', name: 'Red-1' },      // Red-orange
    { wavelength: 633.4, color: '#FF6347', name: 'Red-2' },      // Red
    { wavelength: 626.6, color: '#FF7F50', name: 'Orange-1' },   // Orange
    { wavelength: 621.7, color: '#FFA500', name: 'Orange-2' },   // Orange
    { wavelength: 614.3, color: '#FFB347', name: 'Orange-3' },   // Light orange
    { wavelength: 585.2, color: '#FFD700', name: 'Yellow' }      // Yellow
  ]
}

const PrismSpectrometer = ({
  angle = 90,
  viewingSpectrum = false,
  activeGas = null,
  highlightedLine,
  onAngleChange,
  disabled = false,
  className = ''
}: PrismSpectrometerProps) => {
  const id = useId()
  const [isDragging, setIsDragging] = useState(false)

  const containerStyle: CSSProperties = {
    cursor: disabled ? 'default' : 'default',
    opacity: disabled ? 0.5 : 1,
    transition: 'opacity 0.2s ease'
  }

  // Convert angle to telescope arm rotation
  const telescopeRotation = angle - 90 // Center at 90 degrees

  // Handle angle change via drag or click
  const handleMouseDown = () => {
    if (!disabled && onAngleChange) {
      setIsDragging(true)
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging && onAngleChange) {
      const svg = e.currentTarget
      const rect = svg.getBoundingClientRect()
      const centerX = rect.left + rect.width * 0.5
      const centerY = rect.top + rect.height * 0.6
      const mouseX = e.clientX
      const mouseY = e.clientY

      const newAngle = Math.atan2(centerY - mouseY, mouseX - centerX) * (180 / Math.PI)
      const normalizedAngle = Math.max(0, Math.min(180, 90 - newAngle))
      onAngleChange(Math.round(normalizedAngle * 10) / 10)
    }
  }

  // Get spectral lines for active gas
  const spectralLines = activeGas ? SPECTRAL_LINES[activeGas] : []

  return (
    <svg
      viewBox="0 0 300 220"
      width={400}
      height={320}
      style={containerStyle}
      className={className}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <defs>
        {/* Metal gradient */}
        <linearGradient id={`metal-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#3A3A3A" />
          <stop offset="30%" stopColor="#5A5A5A" />
          <stop offset="70%" stopColor="#4A4A4A" />
          <stop offset="100%" stopColor="#3A3A3A" />
        </linearGradient>

        {/* Prism gradient */}
        <linearGradient id={`prism-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.9)" />
          <stop offset="30%" stopColor="rgba(220,235,250,0.7)" />
          <stop offset="70%" stopColor="rgba(200,220,240,0.8)" />
          <stop offset="100%" stopColor="rgba(180,200,230,0.6)" />
        </linearGradient>

        {/* Rainbow gradient for spectrum */}
        <linearGradient id={`spectrum-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#8B00FF" />
          <stop offset="20%" stopColor="#0000FF" />
          <stop offset="35%" stopColor="#00FFFF" />
          <stop offset="50%" stopColor="#00FF00" />
          <stop offset="65%" stopColor="#FFFF00" />
          <stop offset="80%" stopColor="#FFA500" />
          <stop offset="100%" stopColor="#FF0000" />
        </linearGradient>

        {/* Shadow */}
        <filter id={`shadow-${id}`} x="-5%" y="-5%" width="115%" height="115%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.3" />
        </filter>

        {/* Glow effect */}
        <filter id={`glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <g filter={`url(#shadow-${id})`}>
        {/* Base platform */}
        <ellipse
          cx="150"
          cy="180"
          rx="120"
          ry="25"
          fill="#2A2A2A"
          stroke="#3A3A3A"
          strokeWidth="2"
        />

        {/* Graduated circle (degree scale) */}
        <ellipse
          cx="150"
          cy="140"
          rx="95"
          ry="20"
          fill="#1A1A1A"
          stroke="#4A4A4A"
          strokeWidth="1"
        />

        {/* Degree markings */}
        {Array.from({ length: 19 }, (_, i) => {
          const deg = i * 10
          const radians = (deg - 90) * (Math.PI / 180)
          const x1 = 150 + Math.cos(radians) * 85
          const y1 = 140 + Math.sin(radians) * 17
          const x2 = 150 + Math.cos(radians) * 92
          const y2 = 140 + Math.sin(radians) * 19
          const isMajor = deg % 30 === 0
          return (
            <g key={i}>
              <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="#666"
                strokeWidth={isMajor ? 1.5 : 0.8}
              />
              {isMajor && (
                <text
                  x={150 + Math.cos(radians) * 78}
                  y={140 + Math.sin(radians) * 15 + 3}
                  fontSize="7"
                  fill="#888"
                  textAnchor="middle"
                >
                  {deg}
                </text>
              )}
            </g>
          )
        })}

        {/* Prism (central) */}
        <g transform="translate(150, 120)">
          <polygon
            points="0,-30 26,15 -26,15"
            fill={`url(#prism-${id})`}
            stroke="#7A9BB8"
            strokeWidth="1.5"
          />
          {/* Prism shine */}
          <line
            x1="-8"
            y1="-15"
            x2="-15"
            y2="8"
            stroke="rgba(255,255,255,0.5)"
            strokeWidth="2"
            strokeLinecap="round"
          />
          {/* Light beam through prism (when active) */}
          {viewingSpectrum && activeGas && (
            <g>
              {/* Incoming white light */}
              <line
                x1="-80"
                y1="0"
                x2="-10"
                y2="0"
                stroke="#FFFFCC"
                strokeWidth="2"
                opacity="0.6"
              />
              {/* Dispersed spectrum */}
              <path
                d="M10,0 L60,-20"
                stroke="#FF0000"
                strokeWidth="1"
                opacity="0.7"
              />
              <path
                d="M10,0 L65,-10"
                stroke="#FFFF00"
                strokeWidth="1"
                opacity="0.7"
              />
              <path
                d="M10,0 L70,0"
                stroke="#00FF00"
                strokeWidth="1"
                opacity="0.7"
              />
              <path
                d="M10,0 L65,10"
                stroke="#00FFFF"
                strokeWidth="1"
                opacity="0.7"
              />
              <path
                d="M10,0 L60,20"
                stroke="#8B00FF"
                strokeWidth="1"
                opacity="0.7"
              />
            </g>
          )}
        </g>

        {/* Telescope arm */}
        <g
          transform={`rotate(${telescopeRotation}, 150, 140)`}
          style={{ cursor: onAngleChange && !disabled ? 'grab' : 'default' }}
          onMouseDown={handleMouseDown}
        >
          {/* Arm */}
          <rect
            x="148"
            y="60"
            width="4"
            height="80"
            fill={`url(#metal-${id})`}
            rx="2"
          />

          {/* Telescope eyepiece */}
          <g transform="translate(150, 50)">
            <rect
              x="-12"
              y="-20"
              width="24"
              height="30"
              fill={`url(#metal-${id})`}
              rx="4"
            />
            {/* Eyepiece lens */}
            <ellipse
              cx="0"
              cy="-20"
              rx="8"
              ry="4"
              fill="#1A1A1A"
              stroke="#5A5A5A"
              strokeWidth="1"
            />
            {/* Crosshair indicator */}
            <line x1="-6" y1="-12" x2="6" y2="-12" stroke="#FF5A36" strokeWidth="1" />
            <line x1="0" y1="-18" x2="0" y2="-6" stroke="#FF5A36" strokeWidth="1" />
          </g>

          {/* Pointer */}
          <polygon
            points="150,138 147,145 153,145"
            fill="#FF5A36"
          />
        </g>

        {/* Collimator (left side) */}
        <g transform="translate(50, 120)">
          <rect
            x="-30"
            y="-8"
            width="50"
            height="16"
            fill={`url(#metal-${id})`}
            rx="3"
          />
          {/* Slit */}
          <rect
            x="-30"
            y="-3"
            width="4"
            height="6"
            fill="#1A1A1A"
          />
          {/* Light source attachment point */}
          <circle
            cx="-35"
            cy="0"
            r="8"
            fill="#2A2A2A"
            stroke="#4A4A4A"
            strokeWidth="1"
          />
        </g>

        {/* Digital angle display */}
        <g transform="translate(220, 30)">
          <rect
            x="0"
            y="0"
            width="70"
            height="35"
            fill="#1A1A1A"
            stroke="#4A4A4A"
            strokeWidth="1"
            rx="4"
          />
          <rect
            x="5"
            y="5"
            width="60"
            height="25"
            fill="#001a00"
            rx="2"
          />
          <text
            x="35"
            y="12"
            fontSize="6"
            fill="#00A86B"
            textAnchor="middle"
            fontFamily="monospace"
          >
            ANGLE
          </text>
          <text
            x="35"
            y="26"
            fontSize="12"
            fill="#00FF00"
            textAnchor="middle"
            fontFamily="'Courier New', monospace"
            fontWeight="bold"
            filter={`url(#glow-${id})`}
          >
            {angle.toFixed(1)}°
          </text>
        </g>

        {/* Spectrum viewer (when viewing) */}
        {viewingSpectrum && (
          <g transform="translate(10, 10)">
            <rect
              x="0"
              y="0"
              width="100"
              height="50"
              fill="#0A0A0A"
              stroke="#4A4A4A"
              strokeWidth="1"
              rx="4"
            />
            <text
              x="50"
              y="12"
              fontSize="7"
              fill="#888"
              textAnchor="middle"
            >
              SPECTRUM VIEW
            </text>

            {/* Background spectrum */}
            <rect
              x="5"
              y="18"
              width="90"
              height="25"
              fill="#0A0A0A"
            />

            {/* Spectral lines */}
            {spectralLines.map((line, i) => {
              // Map wavelength to x position (400-700nm -> 0-90px)
              const xPos = 5 + ((line.wavelength - 400) / 300) * 90
              const isHighlighted = highlightedLine?.wavelength === line.wavelength
              return (
                <line
                  key={i}
                  x1={xPos}
                  y1="18"
                  x2={xPos}
                  y2="43"
                  stroke={line.color}
                  strokeWidth={isHighlighted ? 3 : 2}
                  opacity={isHighlighted ? 1 : 0.8}
                  filter={isHighlighted ? `url(#glow-${id})` : undefined}
                />
              )
            })}

            {/* Wavelength scale */}
            <text x="8" y="48" fontSize="5" fill="#666">400</text>
            <text x="45" y="48" fontSize="5" fill="#666">550</text>
            <text x="85" y="48" fontSize="5" fill="#666">700nm</text>
          </g>
        )}
      </g>

      {/* Active gas indicator */}
      {activeGas && (
        <g transform="translate(10, 70)">
          <rect
            x="0"
            y="0"
            width="60"
            height="20"
            fill="#2A2A2A"
            stroke={
              activeGas === 'hydrogen' ? '#FF69B4' :
              activeGas === 'helium' ? '#FFA500' :
              '#FF4500'
            }
            strokeWidth="2"
            rx="4"
          />
          <text
            x="30"
            y="14"
            fontSize="9"
            fill="#E0E0E0"
            textAnchor="middle"
            fontWeight="500"
          >
            {activeGas.charAt(0).toUpperCase() + activeGas.slice(1)}
          </text>
        </g>
      )}

      {/* Label */}
      <text
        x="150"
        y="215"
        fontSize="11"
        fill="#E0E0E0"
        textAnchor="middle"
        fontWeight="500"
      >
        Prism Spectrometer
      </text>
    </svg>
  )
}

export default PrismSpectrometer
